import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "@project/hooks/useAuth";
import { Check, Heart, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@project/components/ui/button";
import { Checkbox } from "@project/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@project/components/ui/radio-group";
import { MobileNumberField } from "@project/components/mobile-number-field";
import { openRazorpayCheckout } from "@project/donate/components/razorpay-checkout";
import {
  PaymentVerificationOverlay,
  type PaymentVerificationStatus,
} from "@project/donate/components/payment-verification-overlay";
import { getApiErrorMessage } from "@project/lib/api-error";
import { formatPreferredTithiOption } from "@project/lib/jain-panchang-utils";
import { publicGaushalaService } from "@project/services/donate/gaushala.service";
import { publicJainPanchangService } from "@project/services/user/jain-panchang.service";
import {
  GAUSHALA_OCCASION_OPTIONS,
  formatGaushalaPrice,
  type GaushalaCreateOrderPayload,
  type GaushalaSponsorshipSelection,
} from "@project/types/gaushala";
import type { JainPanchang } from "@project/types/jain-panchang";

const amountClass =
  "block font-sans text-[1.75rem] font-bold leading-none tracking-tight text-primary";

type FormState = {
  preferred_sponsorship_date: string;
  preferred_tithi: string;
  donor_name: string;
  mobile_number: string;
  email: string;
  city: string;
  full_address: string;
  state: string;
  pincode: string;
  country: string;
  occasion: string;
  display_name: string;
  special_instructions: string;
  is_anonymous: boolean;
  info_confirmed: boolean;
  contact_consent: boolean;
};

function getTodayInKolkata(): string {
  const d = new Date();
  const utc = d.getTime() + d.getTimezoneOffset() * 60000;
  const kolkataDate = new Date(utc + 3600000 * 5.5);
  return kolkataDate.toISOString().slice(0, 10);
}

function formatDisplayDate(iso: string) {
  if (!iso) {
    return "—";
  }
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
}

function emptyForm(minDate: string): FormState {
  return {
    preferred_sponsorship_date: minDate,
    preferred_tithi: "",
    donor_name: "",
    mobile_number: "",
    email: "",
    city: "",
    full_address: "",
    state: "Gujarat",
    pincode: "",
    country: "India",
    occasion: "Birthday",
    display_name: "",
    special_instructions: "",
    is_anonymous: false,
    info_confirmed: false,
    contact_consent: true,
  };
}

export type GaushalaSponsorshipModalProps = {
  open: boolean;
  selection: GaushalaSponsorshipSelection | null;
  onOpenChange: (open: boolean) => void;
};

export function GaushalaSponsorshipModal({
  open,
  selection,
  onOpenChange,
}: GaushalaSponsorshipModalProps) {
  const todayDate = useMemo(() => getTodayInKolkata(), []);
  const [form, setForm] = useState<FormState>(() => emptyForm(todayDate));
  const [submitting, setSubmitting] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [verificationStatus, setVerificationStatus] =
    useState<PaymentVerificationStatus>("idle");
  const [tithiOptions, setTithiOptions] = useState<JainPanchang[]>([]);
  const [tithiLoading, setTithiLoading] = useState(false);
  const [tithiError, setTithiError] = useState<string | null>(null);

  const selectionId = selection && "id" in selection ? selection.id : undefined;

  useEffect(() => {
    if (open) {
      setForm(emptyForm(todayDate));
      setSubmitting(false);
      setVerificationOpen(false);
      setVerificationStatus("idle");
      setTithiOptions([]);
      setTithiError(null);
    }
  }, [open, todayDate, selection?.kind, selectionId]);

  useEffect(() => {
    if (!open || !form.preferred_sponsorship_date) {
      return;
    }

    let ignore = false;
    const loadTithis = async () => {
      setTithiLoading(true);
      setTithiError(null);
      setForm(prev =>
        prev.preferred_tithi ? { ...prev, preferred_tithi: "" } : prev
      );
      try {
        const entries = await publicJainPanchangService.listByEnglishDate(
          form.preferred_sponsorship_date
        );
        if (ignore) {
          return;
        }
        setTithiOptions(entries);
        if (entries.length === 1) {
          const only = formatPreferredTithiOption(entries[0]);
          if (only) {
            setForm(prev => ({ ...prev, preferred_tithi: only }));
          }
        }
      } catch {
        if (!ignore) {
          setTithiOptions([]);
          setTithiError("Could not load tithi options for this date.");
        }
      } finally {
        if (!ignore) {
          setTithiLoading(false);
        }
      }
    };

    void loadTithis();
    return () => {
      ignore = true;
    };
  }, [open, form.preferred_sponsorship_date]);

  const patch = (updates: Partial<FormState>) =>
    setForm(prev => ({ ...prev, ...updates }));

  const tithiRadioOptions = useMemo(
    () =>
      tithiOptions
        .map(entry => ({
          id: entry.id,
          value: formatPreferredTithiOption(entry),
          title: formatPreferredTithiOption({ ...entry, type: undefined }),
          type: entry.type === "hindu" ? "Hindu" : "Jain",
          typeKey: entry.type === "hindu" ? "hindu" : "jain",
        }))
        .filter(option => option.value),
    [tithiOptions]
  );

  const buildPayload = (): GaushalaCreateOrderPayload => {
    if (!selection) {
      throw new Error("No sponsorship selected.");
    }
    if (!form.donor_name.trim()) {
      throw new Error("Please enter your full name.");
    }
    if (!/^\d{10}$/.test(form.mobile_number.trim())) {
      throw new Error("Enter a valid 10-digit mobile number.");
    }
    if (!form.email.trim()) {
      throw new Error("Email is required.");
    }
    if (!form.city.trim()) {
      throw new Error("City is required.");
    }
    if (!form.full_address.trim()) {
      throw new Error("Full address is required.");
    }
    if (!form.state.trim()) {
      throw new Error("State is required.");
    }
    if (!/^\d{6}$/.test(form.pincode.trim())) {
      throw new Error("Enter a valid 6-digit pincode.");
    }
    if (!form.info_confirmed) {
      throw new Error("Please confirm the information to proceed.");
    }
    if (tithiRadioOptions.length > 0 && !form.preferred_tithi.trim()) {
      throw new Error("Please select a preferred tithi.");
    }

    const schemeId =
      "id" in selection
        ? typeof selection.id === "number"
          ? selection.id
          : parseInt(String(selection.id), 10)
        : undefined;

    const numericAmount = parseFloat(selection.price);
    const amountStr = Number.isFinite(numericAmount)
      ? numericAmount.toFixed(2)
      : String(selection.price);

    return {
      scheme_id:
        Number.isFinite(schemeId) && (schemeId ?? 0) > 0 ? schemeId : undefined,
      amount: amountStr,
      mode: "online",
      donor_name: form.donor_name.trim(),
      preferred_sponsorship_date: form.preferred_sponsorship_date,
      preferred_tithi: form.preferred_tithi.trim() || undefined,
      mobile_number: form.mobile_number.trim(),
      email: form.email.trim(),
      city: form.city.trim(),
      full_address: form.full_address.trim(),
      state: form.state.trim(),
      pincode: form.pincode.trim(),
      country: form.country.trim() || "India",
      occasion: form.occasion || undefined,
      display_name: form.display_name.trim() || undefined,
      special_instructions: form.special_instructions.trim() || undefined,
      is_anonymous: form.is_anonymous,
      info_confirmed: form.info_confirmed,
      contact_consent: form.contact_consent,
    };
  };

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleProceed = async () => {
    if (!selection) {
      return;
    }
    if (!isAuthenticated) {
      toast.info("Please log in to continue with your donation");
      onOpenChange(false);
      navigate({
        to: "/login",
        search: { redirectTo: "/gaushala" },
      });
      return;
    }

    try {
      const payload = buildPayload();
      setSubmitting(true);
      const order = await publicGaushalaService.createOrder(payload);

      onOpenChange(false);
      await new Promise(resolve => window.setTimeout(resolve, 80));

      const numericAmount = parseFloat(selection.price);

      await openRazorpayCheckout({
        order_id: order.order_id,
        amount:
          order.amount ||
          (Number.isFinite(numericAmount) ? numericAmount * 100 : 0),
        currency: order.currency ?? "INR",
        key: order.key_id,
        name: "Jivandhara Gaushala",
        description: selection.title,
        prefill: {
          name: payload.donor_name,
          email: payload.email,
          contact: payload.mobile_number,
        },
        onDismiss: () => {
          setSubmitting(false);
          onOpenChange(true);
          toast.message("Payment cancelled. You can try again anytime.");
        },
        onSuccess: async response => {
          setVerificationOpen(true);
          setVerificationStatus("verifying");
          try {
            const verify = await publicGaushalaService.verifyPayment({
              razorpay_order_id: order.order_id,
              razorpay_payment_id: String(response.razorpay_payment_id),
              razorpay_signature: String(response.razorpay_signature),
            });
            setVerificationStatus("success");
            toast.success(
              verify.receipt_no
                ? `Payment successful. Receipt: ${verify.receipt_no}`
                : "Payment successful. Thank you for your Gau Seva sponsorship."
            );
            window.setTimeout(() => {
              setVerificationOpen(false);
              setVerificationStatus("idle");
            }, 1600);
          } catch (verifyError) {
            setVerificationOpen(false);
            setVerificationStatus("idle");
            toast.error(
              getApiErrorMessage(
                verifyError,
                "Payment received but verification failed. Please contact support."
              )
            );
          } finally {
            setSubmitting(false);
          }
        },
      });
    } catch (error) {
      setSubmitting(false);
      onOpenChange(true);
      toast.error(getApiErrorMessage(error, "Could not start payment"));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
          <DialogHeader className="shrink-0 border-b border-border px-5 py-4 pr-12 text-left sm:px-6">
            <div className="flex items-start gap-3">
              <div className="bg-primary/10 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-primary">
                <Heart className="h-5 w-5 fill-current" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-foreground sm:text-xl">
                  Gau Seva Registration
                </DialogTitle>
                <DialogDescription className="mt-0.5 text-sm text-muted-foreground">
                  Gaushala Fodder & Medical Care Sponsorship
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-7 overflow-y-auto px-5 py-5 sm:px-6">
            {/* Selected Sponsorship */}
            <section>
              <h3 className="font-serif text-xl font-semibold text-foreground">
                Selected Sponsorship
              </h3>
              {selection && (
                <div className="border-primary/25 bg-primary/[0.04] mt-3 rounded-2xl border p-4 sm:p-5">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-primary">
                        {selection.categoryLabel ?? "Gaushala Sponsorship"}
                      </p>
                      <p className="mt-1 text-xl font-semibold text-foreground">
                        {selection.title}
                      </p>
                      {selection.description && (
                        <p className="mt-1 text-sm text-muted-foreground">
                          {selection.description}
                        </p>
                      )}
                      {selection.period && (
                        <p className="mt-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                          Period: {selection.period}
                        </p>
                      )}
                    </div>
                    <div className="shrink-0 rounded-xl border border-border bg-card px-4 py-3 text-center shadow-sm">
                      <div className={amountClass}>
                        {formatGaushalaPrice(selection.price)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Sponsorship Date */}
            <section>
              <h3 className="font-serif text-xl font-semibold text-foreground">
                Sponsorship Date
              </h3>
              <div className="mt-3">
                <div className="mb-1.5 flex items-end justify-between gap-2">
                  <Label data-required="true">Preferred Sponsorship Date</Label>
                  <span className="text-[11px] text-muted-foreground">
                    Asia/Kolkata Timezone
                  </span>
                </div>
                <Input
                  type="date"
                  min={todayDate}
                  required
                  value={form.preferred_sponsorship_date}
                  onChange={e =>
                    patch({
                      preferred_sponsorship_date: e.target.value,
                      preferred_tithi: "",
                    })
                  }
                  className="bg-muted/30 rounded-xl"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Selected: {formatDisplayDate(form.preferred_sponsorship_date)}
                </p>
              </div>

              <div className="mt-4">
                <Label data-required={tithiRadioOptions.length > 0}>
                  Preferred Tithi
                </Label>
                {tithiLoading ? (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Loading tithi options…
                  </p>
                ) : tithiError ? (
                  <p className="mt-2 text-sm text-destructive">{tithiError}</p>
                ) : tithiRadioOptions.length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">
                    No tithi entries found for this date.
                  </p>
                ) : (
                  <RadioGroup
                    value={form.preferred_tithi}
                    onValueChange={preferred_tithi =>
                      patch({ preferred_tithi })
                    }
                    className="mt-2 grid gap-3 sm:grid-cols-2"
                  >
                    {tithiRadioOptions.map(option => {
                      const selected = form.preferred_tithi === option.value;
                      return (
                        <label
                          key={option.id}
                          className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 text-sm transition-colors ${
                            selected
                              ? "bg-primary/[0.05] border-primary"
                              : "border-border/70 hover:bg-muted/40 bg-card"
                          }`}
                        >
                          <RadioGroupItem
                            value={option.value}
                            className="mt-0.5"
                          />
                          <span className="min-w-0">
                            <span className="block font-medium leading-snug text-foreground">
                              {option.title}
                            </span>
                            <span
                              className={`mt-2 inline-flex h-5 items-center px-2.5 text-[10px] font-bold uppercase tracking-wide text-primary ${
                                option.typeKey === "hindu"
                                  ? "bg-orange-300"
                                  : "bg-green-300"
                              }`}
                              style={{
                                clipPath:
                                  "polygon(0 0, calc(100% - 5px) 0, 100% 50%, calc(100% - 5px) 100%, 0 100%)",
                              }}
                            >
                              {option.type}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </RadioGroup>
                )}
              </div>
            </section>

            {/* Your Details */}
            <section>
              <h3 className="font-serif text-xl font-semibold text-foreground">
                Your Details
              </h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <Label data-required="true">Donor Name</Label>
                  <Input
                    required
                    value={form.donor_name}
                    onChange={e => patch({ donor_name: e.target.value })}
                    placeholder="Enter your full name"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">Mobile Number</Label>
                  <MobileNumberField
                    required
                    value={form.mobile_number}
                    onChange={mobile_number => patch({ mobile_number })}
                    placeholder="10-digit phone number"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">Email</Label>
                  <Input
                    type="email"
                    required
                    value={form.email}
                    onChange={e => patch({ email: e.target.value })}
                    placeholder="name@example.com"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">City</Label>
                  <Input
                    required
                    value={form.city}
                    onChange={e => patch({ city: e.target.value })}
                    placeholder="Ahmedabad"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label data-required="true">Full Address</Label>
                  <Textarea
                    required
                    rows={2}
                    value={form.full_address}
                    onChange={e => patch({ full_address: e.target.value })}
                    placeholder="Street name, society, flat number"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">State</Label>
                  <Input
                    required
                    value={form.state}
                    onChange={e => patch({ state: e.target.value })}
                    placeholder="Gujarat"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">Pincode</Label>
                  <Input
                    required
                    inputMode="numeric"
                    maxLength={6}
                    value={form.pincode}
                    onChange={e =>
                      patch({
                        pincode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      })
                    }
                    placeholder="380015"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Country</Label>
                  <Input
                    value={form.country}
                    onChange={e => patch({ country: e.target.value })}
                    placeholder="India"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
              </div>
            </section>

            {/* Sponsorship Details */}
            <section>
              <h3 className="font-serif text-xl font-semibold text-foreground">
                Sponsorship Details
              </h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Occasion</Label>
                  <Select
                    value={form.occasion}
                    onValueChange={occasion => patch({ occasion })}
                  >
                    <SelectTrigger className="bg-muted/30 rounded-xl">
                      <SelectValue placeholder="Select occasion" />
                    </SelectTrigger>
                    <SelectContent>
                      {GAUSHALA_OCCASION_OPTIONS.map(option => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label>Name to Display on Board</Label>
                  <Input
                    value={form.display_name}
                    onChange={e => patch({ display_name: e.target.value })}
                    placeholder="In loving memory of... / On behalf of..."
                    className="bg-muted/30 rounded-xl"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Enter the donor, family or commemorative name that may be
                    displayed for this Gau Seva.
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <div className="mb-1.5 flex items-center justify-between">
                    <Label>Special Instructions</Label>
                    <span className="text-[11px] text-muted-foreground">
                      {form.special_instructions.length}/500
                    </span>
                  </div>
                  <Textarea
                    rows={3}
                    maxLength={500}
                    value={form.special_instructions}
                    onChange={e =>
                      patch({ special_instructions: e.target.value })
                    }
                    placeholder="E.g., Special fodder distribution note or Gau Seva instructions..."
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="bg-muted/20 flex cursor-pointer items-start gap-3 rounded-xl border border-border px-4 py-3">
                    <Checkbox
                      checked={form.is_anonymous}
                      onCheckedChange={checked =>
                        patch({ is_anonymous: checked === true })
                      }
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-foreground">
                        Keep my donation anonymous
                      </span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        Admin can still securely retain the donor&apos;s
                        submitted details. Public recognition will display
                        &apos;Anonymous Donor&apos;.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            </section>

            {/* Consent */}
            <section>
              <h3 className="font-serif text-xl font-semibold text-foreground">
                Consent
              </h3>
              <div className="bg-muted/20 mt-3 space-y-3 rounded-xl border border-border px-4 py-3">
                <label className="flex cursor-pointer items-start gap-3">
                  <Checkbox
                    checked={form.info_confirmed}
                    onCheckedChange={checked =>
                      patch({ info_confirmed: checked === true })
                    }
                    className="mt-0.5"
                  />
                  <span className="text-sm text-foreground">
                    I confirm that the information provided is correct and agree
                    to proceed with this sponsorship.{" "}
                    <span className="text-destructive">*</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3">
                  <Checkbox
                    checked={form.contact_consent}
                    onCheckedChange={checked =>
                      patch({ contact_consent: checked === true })
                    }
                    className="mt-0.5"
                  />
                  <span className="text-sm text-foreground">
                    I agree to be contacted regarding this Gau Seva sponsorship.
                    (Optional)
                  </span>
                </label>
              </div>
            </section>
          </div>

          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-border px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              disabled={submitting}
              onClick={() => onOpenChange(false)}
            >
              <X className="mr-1 h-4 w-4" />
              Cancel
            </Button>
            <Button
              type="button"
              className="hover:bg-primary/90 rounded-xl bg-primary text-primary-foreground"
              disabled={submitting || !selection}
              onClick={() => void handleProceed()}
            >
              <Check className="mr-1 h-4 w-4" />
              {submitting ? "Processing…" : "Proceed to Payment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PaymentVerificationOverlay
        open={verificationOpen}
        status={verificationStatus}
      />
    </>
  );
}
